import { SnackbarOrigin } from '@quillbot/design-system';
import { StyledComponentProps } from '@mui/styles';
import dynamic from 'next/dynamic';
import QuillLocalStorage from 'core/utils/localStorageClient';
import { QUILLPRODUCTS } from '@Common/models';
import Track from './track';
import { getReduxStore } from '../redux/store';
import {
  toggleDrawerAction,
  toggleToastrAction,
  toggleAlertDialogAction,
  toggleSessionDialogAction,
  toggleSignUpModel,
  setCrossProductUpsell,
} from '../actions/commonActions';
import { QuillDrawer, RootState } from '../models/redux/store';

// const FeedbackDrawer =
// setting feedack drawer as initial drawer content
let currentDrawerContent = dynamic(() => import('../components/drawer/FeedbackDrawer'), {
  ssr: false,
});
let toastrAction: { action: VoidFunction; actionText: string } | null = null;
let alertAction: {
  dialogCloseHandler: VoidFunction;
  closeButtonClickHandler: React.ReactEventHandler<{}> | VoidFunction;
} | null = null;

export const openDrawer = (
  drawerName: QuillDrawer,
  drawerView: React.ComponentType<
    Pick<{}, never> & StyledComponentProps<'root' | 'feedbackTitle' | 'feedbackBtn' | 'discordBtn'>
  >
) => {
  currentDrawerContent = drawerView;
  getReduxStore()?.dispatch(toggleDrawerAction(drawerName));
};

export const openSignUpModel = (isOpenSignUpModel: boolean) => {
  getReduxStore()?.dispatch(toggleSignUpModel(isOpenSignUpModel));
};

export const openSessionDialog = () =>
  getReduxStore()?.dispatch(toggleSessionDialogAction({ isShown: true }));

export const openToastr = (
  message: string,
  timeout = 3000,
  action?: VoidFunction,
  actionText?: string,
  position?: SnackbarOrigin
) => {
  if ((getReduxStore()?.getState() as RootState)?.common?.quillToast?.isShown) return;
  if (action && actionText) {
    toastrAction = {
      action: action || (() => {}),
      actionText,
    };
  } else toastrAction = null;
  const isMobile = (getReduxStore()?.getState() as RootState)?.common.platform.isMobile;
  const isExtension = (getReduxStore()?.getState() as RootState)?.common.platform.isExtension;
  position = isMobile || isExtension ? { vertical: 'bottom', horizontal: 'left' } : position;
  getReduxStore()?.dispatch(toggleToastrAction(message, timeout, true, position));
};

export const openAlertDialog = (
  isBackdropDissmissalAllowed: boolean,
  title: string,
  content: string | string[],
  alertButtonLabel?: string,
  onCloseActionButtonClicked?: React.ReactEventHandler<{}>,
  handleDialogClose?: VoidFunction
) => {
  if ((getReduxStore()?.getState() as RootState)?.common?.alertDialog?.isShown) return;
  const voidFunction = () => {};
  alertAction = {
    closeButtonClickHandler: onCloseActionButtonClicked || voidFunction,
    dialogCloseHandler: handleDialogClose || voidFunction,
  };

  getReduxStore()?.dispatch(
    toggleAlertDialogAction(
      isBackdropDissmissalAllowed,
      content,
      title,
      true,
      alertButtonLabel || '' // It shows undefined for a very brief moment otherwise
    )
  );
};

export const getDrawerItem = () => currentDrawerContent;

export const getToastrAction = (): { action: VoidFunction; actionText: string } | null =>
  toastrAction;

export const getAlertAction = () => alertAction;

/**
 * Method to change keywords casing if it's matched in the text.
 */
export const handleKeywordCasing = (keywords: string[], text: string): string[] => {
  const caseChangedkeywords = keywords.map((keyword: string) => {
    let caseKeyword = keyword;
    const regex = new RegExp(`(^| )(${keyword})(?=[ ?.,!]|$)`, 'gim');
    let keywordMatch: any = text.match(regex);

    keywordMatch = keywordMatch?.map((kw: string) => kw.trim());

    if (keywordMatch?.length) {
      caseKeyword = keywordMatch.sort((a: string, b: string) => (a < b ? 1 : -1))[0];
    }
    return caseKeyword;
  });

  return caseChangedkeywords;
};

export const toggleDrawer = (
  drawerName: QuillDrawer,
  drawerContent: React.ComponentType<
    Pick<{}, never> & StyledComponentProps<'root' | 'feedbackTitle' | 'feedbackBtn' | 'discordBtn'>
  >
) => {
  const openedDrawer = (getReduxStore()?.getState() as RootState)?.common.quillDrawer;
  if (drawerName === openedDrawer) {
    getReduxStore()?.dispatch(toggleDrawerAction(null));
    Track.sideDrawerStateChanged('closed', drawerName);
  } else {
    openDrawer(drawerName, drawerContent);
    Track.sideDrawerStateChanged('opened', drawerName);
  }
};

const upsellPPHR = () => {
  const userData = getReduxStore()?.getState()?.user;

  if (
    !userData?.counts?.visitedPageCounts?.paraphraser &&
    QuillLocalStorage.getInstance().get('pphrUpsell', 'false') === 'false'
  ) {
    getReduxStore()?.dispatch(setCrossProductUpsell(QUILLPRODUCTS.PARAPHRASER));
  }
};

const upsellPlagCheck = () => {
  const userData = getReduxStore()?.getState()?.user;

  if (
    QuillLocalStorage.getInstance().get('plagCheckUpsell', 'false') === 'false' &&
    !userData?.counts?.visitedPageCounts?.plagiarismchecker
  ) {
    getReduxStore()?.dispatch(setCrossProductUpsell(QUILLPRODUCTS.PLAGIARISM_CHECKER));
  }
};

export const crossProductUpsell = (currentProductPage: QUILLPRODUCTS) => {
  const userData = getReduxStore()?.getState()?.user;
  if (userData?.isAuthenticated) {
    if (currentProductPage === QUILLPRODUCTS.PARAPHRASER) {
      if (userData?.counts?.submittedInputCount && userData?.counts?.submittedInputCount >= 35) {
        upsellPlagCheck();
      }
    }
    if (currentProductPage === QUILLPRODUCTS.GRAMMAR_CHECKER) {
      if (
        userData?.counts?.grammerChecker?.pastedTextCount &&
        userData?.counts?.grammerChecker?.pastedTextCount >= 10
      ) {
        if (userData?.counts?.grammerChecker?.pastedTextCount >= 35) upsellPlagCheck();
        upsellPPHR();
      }
    }
  }
};
